import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type Props = {
  content: string;
  /** User bubbles use inverted colors */
  variant?: 'assistant' | 'user';
};

/**
 * Renders assistant (and user) chat content as GitHub-flavored markdown:
 * headings, bold, lists, and tables with readable styling.
 */
export function ChatMarkdown({ content, variant = 'assistant' }: Props) {
  const isUser = variant === 'user';

  return (
    <div
      className={
        isUser
          ? 'chat-md chat-md-user text-sm leading-relaxed'
          : 'chat-md chat-md-assistant text-sm leading-relaxed text-gray-900 dark:text-gray-100'
      }
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h3 className="text-base font-semibold mt-3 mb-2 first:mt-0">{children}</h3>
          ),
          h2: ({ children }) => (
            <h3 className="text-base font-semibold mt-3 mb-2 first:mt-0">{children}</h3>
          ),
          h3: ({ children }) => (
            <h4 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h4>
          ),
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="list-disc pl-5 mb-2 space-y-1 last:mb-0">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 mb-2 space-y-1 last:mb-0">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-snug">{children}</li>,
          strong: ({ children }) => (
            <strong className={isUser ? 'font-semibold' : 'font-semibold text-gray-950 dark:text-white'}>
              {children}
            </strong>
          ),
          em: ({ children }) => <em className="italic opacity-90">{children}</em>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className={
                isUser
                  ? 'underline underline-offset-2'
                  : 'text-primary-600 dark:text-primary-400 underline underline-offset-2'
              }
            >
              {children}
            </a>
          ),
          code: ({ className, children }) => {
            const isBlock = Boolean(className);
            if (isBlock) {
              return (
                <code className="block text-xs font-mono whitespace-pre overflow-x-auto rounded-lg bg-black/10 dark:bg-black/40 px-3 py-2 my-2">
                  {children}
                </code>
              );
            }
            return (
              <code
                className={
                  isUser
                    ? 'font-mono text-[0.85em] bg-white/20 px-1 py-0.5 rounded'
                    : 'font-mono text-[0.85em] bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded'
                }
              >
                {children}
              </code>
            );
          },
          pre: ({ children }) => <pre className="my-2 overflow-x-auto">{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote
              className={
                isUser
                  ? 'border-l-2 border-white/50 pl-3 my-2 opacity-90'
                  : 'border-l-2 border-gray-300 dark:border-gray-600 pl-3 my-2 text-gray-700 dark:text-gray-300'
              }
            >
              {children}
            </blockquote>
          ),
          hr: () => (
            <hr
              className={
                isUser
                  ? 'my-3 border-white/30'
                  : 'my-3 border-gray-200 dark:border-gray-700'
              }
            />
          ),
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
              <table className="min-w-full text-left text-xs sm:text-sm border-collapse">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead
              className={
                isUser
                  ? 'bg-white/15'
                  : 'bg-gray-200/80 dark:bg-gray-700/80'
              }
            >
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody
              className={
                isUser
                  ? 'divide-y divide-white/15'
                  : 'divide-y divide-gray-200 dark:divide-gray-700'
              }
            >
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr
              className={
                isUser
                  ? 'even:bg-white/5'
                  : 'even:bg-white/60 dark:even:bg-gray-800/40'
              }
            >
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 font-semibold whitespace-nowrap">{children}</th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 align-top">{children}</td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
